import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Download, Play, Pause, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useConnectionVersion } from '@/hooks/useConnectionVersion';
import { useTranslation } from "react-i18next";

interface AudioMessageProps {
  messageId: string;
  waMessageId: string;
  existingProcessedUrl?: string | null;
  transcription?: string | null;
  onUrlProcessed?: (url: string) => void;
  onTranscriptionProcessed?: (transcription: string) => void;
  isSending?: boolean;
}

const AudioMessage: React.FC<AudioMessageProps> = ({
  messageId,
  waMessageId,
  existingProcessedUrl,
  transcription,
  onUrlProcessed,
  onTranscriptionProcessed,
  isSending = false
}) => {
  const { t } = useTranslation("chat");
  const { user } = useAuth();
  const { getUrl } = useConnectionVersion();
  const [audioUrl, setAudioUrl] = useState<string | null>(existingProcessedUrl || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const instance = (user?.account as any)?.instance;
  const apiKey = (user?.account as any)?.api_token;

  // Check if URL is from WhatsApp (mmg.whatsapp.net) - these need processing
  const isWhatsAppUrl = audioUrl && typeof audioUrl === 'string' && audioUrl.includes('mmg.whatsapp.net');
  const needsProcessing = !audioUrl || isWhatsAppUrl;

  useEffect(() => {
    if (existingProcessedUrl) {
      setAudioUrl(existingProcessedUrl);
    }
  }, [existingProcessedUrl]);

  const downloadAndConvertAudio = async () => {
    if (!supabase) {
      setError(t("common.supabaseUnavailable"));
      return;
    }

    console.log('🎵 [AudioMessage] Starting audio processing');
    console.log('  - messageId:', messageId);
    console.log('  - waMessageId:', waMessageId);

    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const edgeFunctionUrl = getUrl('process-media-from-payload');

      console.log('📤 [AudioMessage] Calling edge function:', edgeFunctionUrl);

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messageId: messageId
        })
      });

      console.log('📥 [AudioMessage] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ [AudioMessage] Error response:', errorData);
        throw new Error(errorData.error || 'Erro ao buscar áudio');
      }

      const data = await response.json();
      console.log('✅ [AudioMessage] Data received:', {
        hasBase64: !!data.base64,
        base64Length: data.base64?.length,
        mimetype: data.mimetype,
        fileName: data.fileName,
        media_kind: data.media_kind,
        hasTranscription: !!data.transcription
      });

      if (!data.base64) {
        throw new Error(t("common.base64Missing"));
      }

      // If transcription was received, update via callback
      if (data.transcription) {
        console.log('✅ [AudioMessage] Transcription received:', data.transcription);
        if (onTranscriptionProcessed) {
          onTranscriptionProcessed(data.transcription);
        }
      }

      const base64Data = data.base64.includes(',')
        ? data.base64.split(',')[1]
        : data.base64;

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const mimeType = data.mimetype || 'audio/ogg';
      const blob = new Blob([byteArray], { type: mimeType });

      const getExtensionFromMimeType = (mime: string) => {
        if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
        if (mime.includes('ogg')) return 'ogg';
        if (mime.includes('wav')) return 'wav';
        if (mime.includes('aac')) return 'aac';
        if (mime.includes('webm')) return 'webm';
        return 'mp3';
      };

      const extension = getExtensionFromMimeType(mimeType);
      const fileName = data.fileName || `audio_${Date.now()}.${extension}`;
      const filePath = `${messageId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chat-audios')
        .upload(filePath, blob, {
          contentType: mimeType,
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from('chat-audios')
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;
      setAudioUrl(publicUrl);

      if (onUrlProcessed) {
        onUrlProcessed(publicUrl);
      }

      await supabase
        .from('account_messages')
        .update({
          processed_media_url: publicUrl,
          media_url: publicUrl
        })
        .eq('id', messageId);

    } catch (err: any) {
      console.error('Erro ao processar áudio:', err);
      setError(err.message || 'Erro ao processar áudio');
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // Show sending state
  if (isSending) {
    return (
      <div className="flex items-center gap-3 p-3 bg-black/5 dark:bg-white/10 rounded-lg">
        <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center flex-shrink-0">
          <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-1">
            <div className="w-1 h-3 bg-blue-500/50 rounded animate-pulse" />
            <div className="w-1 h-4 bg-blue-500/50 rounded animate-pulse delay-75" />
            <div className="w-1 h-5 bg-blue-500/50 rounded animate-pulse delay-150" />
            <div className="w-1 h-4 bg-blue-500/50 rounded animate-pulse delay-75" />
            <div className="w-1 h-3 bg-blue-500/50 rounded animate-pulse" />
          </div>
          <span className="text-sm text-neutral-600 dark:text-neutral-300">{t("area.sendingAudio")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 p-3 bg-red-900/20 rounded-lg border border-red-800/30">
          <span className="text-sm text-red-400">{error}</span>
        </div>
        <Button
          onClick={downloadAndConvertAudio}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
        >
          {t("common.tryAgain")}
        </Button>
      </div>
    );
  }

  if (needsProcessing) {
    return (
      <div className="flex items-center gap-2">
        <Button
          onClick={downloadAndConvertAudio}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          size="sm"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("common.processing")}
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              {t("area.downloadAudio")}
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-3 p-2 bg-black/5 dark:bg-white/10 rounded-lg">
        <Button
          onClick={togglePlay}
          className="bg-blue-600 hover:bg-blue-700 text-white w-9 h-9 p-0 rounded-full flex items-center justify-center flex-shrink-0"
          size="sm"
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </Button>

        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleAudioEnded}
          className="flex-1 h-8"
          controls
          controlsList="nodownload"
        />
      </div>

      {transcription && (
        <div className="px-2 py-1.5 bg-black/5 dark:bg-white/10 rounded text-xs text-neutral-700 dark:text-neutral-200 italic">
          {transcription}
        </div>
      )}

      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            const link = document.createElement('a');
            link.href = audioUrl;
            link.download = `audio_${messageId}.mp3`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              try {
                if (link.parentNode === document.body) {
                  document.body.removeChild(link);
                }
              } catch (cleanupError) {
                console.warn('Download link cleanup warning:', cleanupError);
              }
            }, 100);
          } catch (err) {
            console.error('Download error:', err);
          }
        }}
        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 self-start bg-transparent border-0 cursor-pointer p-0"
      >
        <Download className="w-3 h-3" />
        {t("area.download")}
      </button>
    </div>
  );
};

export default AudioMessage;
